import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Marker,
} from "@googlemaps/markerclusterer";
import { InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useState } from "react";
import Avatar from "../../components/Avatar/Avatar";
import { Col, Row } from "../../components/Styled/Container";
import { StyledLink } from "../../components/Styled/StyledLink";
import Text from "../../components/Styled/Text";
import { UserActivityMapData } from "../../graphql/graphql";
import { calculateAge, timeSinceString } from "../../util/helpers";
import { PoiMarker } from "./PoiMarker";

export type Poi = {
  key: string;
  location: google.maps.LatLngLiteral;
  userActivityMapData: UserActivityMapData;
};

export type GroupedPoi = {
  key: string;
  location: google.maps.LatLngLiteral;
  users: UserActivityMapData[];
};

const ClusteredPoiMarkers = (props: { pois: Poi[] }) => {
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [selectedPoiKeys, setSelectedPoiKeys] = useState<string[]>([]);
  const [infoWindowPosition, setInfoWindowPosition] =
    useState<google.maps.LatLngLiteral | null>(null);

  const selectedPois = useMemo(
    () =>
      props.pois && selectedPoiKeys.length > 0
        ? props.pois.filter((t) => selectedPoiKeys.includes(t.key))
        : [],
    [props.pois, selectedPoiKeys]
  );

  // Get current poi keys
  const currentPoiKeys = useMemo(
    () => new Set(props.pois.map((poi) => poi.key)),
    [props.pois]
  );

  // Clean up markers that are no longer in pois
  useEffect(() => {
    setMarkers((prevMarkers) => {
      const newMarkers = { ...prevMarkers };
      let changed = false;

      // Remove markers that are no longer in the current pois
      Object.keys(newMarkers).forEach((key) => {
        if (!currentPoiKeys.has(key)) {
          delete newMarkers[key];
          changed = true;
        }
      });

      return changed ? newMarkers : prevMarkers;
    });

    // Close info window if selected poi is no longer available
    setSelectedPoiKeys((prevKeys) => {
      const validKeys = prevKeys.filter((key) => currentPoiKeys.has(key));
      if (validKeys.length !== prevKeys.length) {
        if (validKeys.length === 0) {
          setInfoWindowPosition(null);
        }
        return validKeys;
      }
      return prevKeys;
    });
  }, [currentPoiKeys]);

  // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap();

  const handleClusterClick = useCallback(
    (_event: any, cluster: any, _map: google.maps.Map) => {
      const clusterMarkers = cluster.markers;
      const keys = clusterMarkers
        .map((marker: any) => {
          // Find the key for this marker
          return Object.keys(markers).find((key) => markers[key] === marker);
        })
        .filter(Boolean);

      if (keys.length > 0) {
        setSelectedPoiKeys(keys);
        setInfoWindowPosition(cluster.position.toJSON());
      }
    },
    [markers]
  );

  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({
      map,
      onClusterClick: handleClusterClick,
      algorithm: new SuperClusterAlgorithm({
        radius: 50,
        maxZoom: 16,
      }),
      renderer: {
        render: ({ count, position }) => {
          const size = 32;
          const backgroundColor = "#1976d2"; // Darker blue

          const clusterDiv = document.createElement("div");
          clusterDiv.style.width = `${size}px`;
          clusterDiv.style.height = `${size}px`;
          clusterDiv.style.borderRadius = "50%";
          clusterDiv.style.backgroundColor = backgroundColor;
          clusterDiv.style.border = "3px solid white";
          clusterDiv.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.3)";
          clusterDiv.style.display = "flex";
          clusterDiv.style.alignItems = "center";
          clusterDiv.style.justifyContent = "center";
          clusterDiv.style.color = "white";
          clusterDiv.style.fontSize = "15px";
          clusterDiv.style.fontWeight = "700";
          clusterDiv.style.cursor = "pointer";
          clusterDiv.style.transition = "transform 0.2s ease";
          clusterDiv.textContent = String(count);

          // Add hover effect
          clusterDiv.onmouseenter = () => {
            clusterDiv.style.transform = "scale(1.1)";
          };
          clusterDiv.onmouseleave = () => {
            clusterDiv.style.transform = "scale(1)";
          };

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterDiv,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      },
    });
  }, [map, handleClusterClick]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clusterer) {
        clusterer.clearMarkers();
      }
    };
  }, [clusterer]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleMarkerClick = useCallback((poi: Poi) => {
    setSelectedPoiKeys([poi.key]);
    setInfoWindowPosition(poi.location);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedPoiKeys([]);
    setInfoWindowPosition(null);
  }, []);

  const handleZoom = useCallback(
    (poi: Poi) => {
      map?.setCenter(poi.location);
      map?.setZoom(12);
    },
    [map]
  );

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {props.pois.map((poi) => (
        <PoiMarker
          key={poi.key}
          poi={poi}
          onClick={handleMarkerClick}
          setMarkerRef={setMarkerRef}
        />
      ))}

      {selectedPoiKeys.length > 0 && infoWindowPosition && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={handleInfoWindowClose}
        >
          <Col
            padding={0}
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="hide-scrollbar"
          >
            {selectedPois.length > 1 && (
              <Row style={{ marginBottom: "10px" }}>
                <Text size={16} weight={600} color={"#000"}>
                  {selectedPois.length} users at this location
                </Text>
              </Row>
            )}
            {selectedPois.map((poi, index) => (
              <Col
                key={poi.key}
                padding={0}
                style={{
                  marginBottom: index < selectedPois.length - 1 ? "20px" : "0",
                  paddingBottom: index < selectedPois.length - 1 ? "20px" : "0",
                  borderBottom:
                    index < selectedPois.length - 1 ? "1px solid #ccc" : "none",
                }}
              >
                <Row>
                  <Avatar
                    name={poi.userActivityMapData.username ?? ""}
                    url={
                      poi.userActivityMapData.photo?.uploadComplete
                        ? poi.userActivityMapData.photo?.thumbnailUrl
                        : ""
                    }
                    sx={{ width: "60px", height: "60px" }}
                  />
                </Row>
                <Row>
                  <StyledLink to={`/user/${poi.userActivityMapData.userId}`}>
                    <Text size={16} weight={600} color={"#000"}>
                      {poi.userActivityMapData.username} {"("}
                      {calculateAge(poi.userActivityMapData.birthDate)}
                      {")"}
                    </Text>
                  </StyledLink>
                </Row>
                <Row>
                  <Text size={14} weight={400} color={"#000"}>
                    {poi.userActivityMapData.city} {" | "}{" "}
                    {poi.userActivityMapData.country}
                  </Text>
                </Row>
                <Row>
                  <Text size={12} weight={400} color={"#000"}>
                    Active{" "}
                    {timeSinceString(
                      new Date(poi.userActivityMapData.lastActive)
                    )}
                    {" | "}
                    Created{" "}
                    {timeSinceString(new Date(poi.userActivityMapData.created))}
                  </Text>
                </Row>
                <Row>
                  <Text size={12} weight={400} color={"#000"}>
                    {poi.userActivityMapData.currentAppVersion ?? "N/A"}
                    {" | "}
                    {poi.userActivityMapData.appPlatform ?? "N/A"}
                  </Text>
                </Row>
                {poi.userActivityMapData.premium && (
                  <Row>
                    <Text size={12} weight={600} color={"#000"}>
                      Premium
                    </Text>
                    {poi.userActivityMapData.vip && (
                      <Text size={12} weight={600} color={"#000"}>
                        {" | "} VIP
                      </Text>
                    )}
                  </Row>
                )}
                <Row style={{ marginTop: "8px" }}>
                  <StyledLink
                    to=""
                    onClick={() => handleZoom(poi)}
                  >
                    Zoom to user
                  </StyledLink>
                </Row>
              </Col>
            ))}
            <Row style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #ccc" }}>
              <StyledLink
                to=""
                onClick={() => {
                  map?.setZoom(3.1);
                  map?.setCenter({
                    lat: 35.613266732528366,
                    lng: 9.880013000000014,
                  });
                  handleInfoWindowClose();
                }}
              >
                Reset map
              </StyledLink>
            </Row>
          </Col>
        </InfoWindow>
      )}
    </>
  );
};
export default ClusteredPoiMarkers;
